import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import SectionLoader from "../../../components/SectionLoader";
import { useNavigate } from 'react-router-dom';
import { clearAuthStorage } from "../../../interceptor";

const ErrorLogs = () => {
    const [errorLogs, setErrorLogs] = useState([]);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentRangeStart, setCurrentRangeStart] = useState(1);
    const pagesToShow = 10;
    const offset = useRef(0);
    const [siteDataLength, setSiteDataLength] = useState(0)
    const [selectedFilter, setSelectedFilter] = useState("All");
    const [uniqueWebsites, setUniqueWebsites] = useState([]);
    const [uniqueWebsitesWithIds, setUniqueWebsitesWithIds] = useState([]);
    const [isPageLimitFetched, setIsPageLimitFetched] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const isUniqueWebsitesFetched = useRef(false);
    const token = sessionStorage.getItem("revizeTrainingToken");
    const errorLogsApi = `${process.env.REACT_APP_SERVER_URL}/error_logs`;
    const getWebsiteNameApi = `${process.env.REACT_APP_SERVER_URL}/fetch_website_names`
    const getPageLimitApi = `${process.env.REACT_APP_SERVER_URL}/api/get_page_limit/`;
    const logs_count_url = `${process.env.REACT_APP_SERVER_URL}/error_logs_count`
    const setPageLimitURL = `${process.env.REACT_APP_SERVER_URL}/api/set-page-limit/`;

    const navigate = useNavigate();

    const headers = {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
    };

    // Icons
    const RefreshIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a6acf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 2v6h6"></path>
            <path d="M21 12a9 9 0 0 0-15-6.7L3 8"></path>
            <path d="M21 22v-6h-6"></path>
            <path d="M3 12a9 9 0 0 0 15 6.7L21 16"></path>
        </svg>
    );

    const BackIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
            <line x1="5" y1="6" x2="5" y2="18"></line>
        </svg>
    );

    useEffect(() => {
        fetchCount(selectedFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isPageLimitFetched) {
            const offset = (currentPage - 1) * itemsPerPage;
            setErrorLogs([]);
            fetchErrorLogs(offset,selectedFilter === "All" ? "All" : uniqueWebsitesWithIds[selectedFilter]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, isPageLimitFetched,itemsPerPage]);

    const fetchCount = async (selectedFilter) => {
        const headers = { "Content-Type": "application/json", "Authorization": "Bearer " + token };
        let url = logs_count_url;
        try {
            const response = await axios.get(url, { 
                headers, 
                params: { selectedFilter }
            });
            setSiteDataLength(response.data)
        } catch (err) {
            console.error('Error fetching count:', err);
            throw err;
        }
    };

    // Fetch the page limit when the component mounts
    useEffect(() => {
        const fetchPageLimit = async () => {
            const loggedInUserId = sessionStorage.getItem("loggedInUserId");
            if (!loggedInUserId) return;

            try {
                const response = await axios.get(getPageLimitApi, {
                    headers: headers,
                    params: { user_id: loggedInUserId },
                });

                const pageLimitData = response.data;
                setItemsPerPage(pageLimitData.page_limit);
                setIsPageLimitFetched(true);
            } catch (error) {
                if (error.response) {
                    console.error("Error fetching page limit:", error.response.data.detail);
                } else {
                    console.error("Error fetching page limit:", error);
                }
            }
        };

        if (sessionStorage.getItem("current_page_limit") == null) {
            fetchPageLimit();
        }else{
            setItemsPerPage(sessionStorage.getItem("current_page_limit"))
            setIsPageLimitFetched(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const setPageLimit = async () => {
          const headers = {
            "accept": "application/json",
            "Authorization": "Bearer " + token
          };
          const loggedInUserId = sessionStorage.getItem("loggedInUserId");
      
          if (itemsPerPage != null) {
            try {
              await axios.post(setPageLimitURL,
                { page_limit: itemsPerPage },
                {
                  headers: headers,
                  params: { user_id: loggedInUserId }
                }
              );
      
              sessionStorage.setItem('current_page_limit', itemsPerPage);
              sessionStorage.setItem('previous_page_limit', itemsPerPage);
              
            } catch (setLimitErr) {
              console.error('Error setting page limit:', setLimitErr);
            }
          }
        };
      
        setPageLimit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [itemsPerPage]);

    const fetchWebsiteNames = async () => {
        try {
            const response = await axios.get(getWebsiteNameApi, {
                headers: headers,
            });
    
            const websiteData = response.data;
            const websiteNames = Object.keys(websiteData);
    
            setUniqueWebsitesWithIds(websiteData)
            setUniqueWebsites(["All", ...websiteNames]);
            isUniqueWebsitesFetched.current = true;
        } catch (error) {
            console.error("Error fetching website names:", error);
        }
    };    
    
    // Fetch error logs based on offset
    const fetchErrorLogs = async (offset,selectedFilter) => {
        const skip = offset;
        setIsLoading(true);
        try {
            const response = await axios.get(errorLogsApi, {
                headers: headers,
                params: { skip: skip, limit: itemsPerPage,selectedFilter:selectedFilter },
            });

            const logsData = response.data;
            setErrorLogs(logsData);

            if (!isUniqueWebsitesFetched.current){
                await fetchWebsiteNames();
            }
        } catch (error) {
            console.error("Error fetching error logs:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Refresh functionality
    const handleRefresh = async () => {
        const newOffset = (currentPage - 1) * itemsPerPage;
        offset.current = newOffset;
        await fetchErrorLogs(newOffset, selectedFilter === "All" ? "All" : uniqueWebsitesWithIds[selectedFilter]);
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return "N/A";
        return text.length > maxLength
            ? `${text.substring(0, maxLength)}...`
            : text;
    };

    const openFullMessageModal = (log) => {
        setSelectedLog(log);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedLog(null);
    };

    let totalPages = Math.ceil(siteDataLength / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handlers for pagination range navigation
    const handleNextRange = () => {
        const newStart = currentRangeStart + pagesToShow;
        if (newStart <= totalPages) {
            setCurrentRangeStart(newStart);
        }
    };

    const handlePrevRange = () => {
        const newStart = currentRangeStart - pagesToShow;
        if (newStart > 0) {
            setCurrentRangeStart(newStart);
        }
    };

    // Page numbers based on current range
    const visiblePageNumbers = Array.from(
        { length: Math.min(pagesToShow, totalPages - currentRangeStart + 1) },
        (_, index) => currentRangeStart + index
    );

    const userApi = `${process.env.REACT_APP_SERVER_URL}/api/users/me`;

    useEffect(() => {
        if (!token) {
            window.location.href = '/auth/sign-in';
        }
        if (token) {
            const requestOptions = {
                method: "GET",
                headers: { "accept": "application/json", "Authorization": "Bearer " + token },
            };

            fetch(userApi, requestOptions)
                .then(function (response) {
                    return response.json();
                }).then(function (data) {
                    if (data.is_active === false) {
                        clearAuthStorage();
                        window.location.href = '/auth/sign-in';
                    }
                }).catch(err => {
                    console.log("error", err);
                    clearAuthStorage();
                    window.location.href = '/auth/sign-in';
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleFilterChange = (value) => {
        setSelectedFilter(value);
        setCurrentPage(1);
        setCurrentRangeStart(1);
        fetchCount(value);
        totalPages = Math.ceil(siteDataLength / itemsPerPage);
        setErrorLogs([]);
        offset.current = 0
        fetchErrorLogs(offset.current,value === "All" ? "All" : uniqueWebsitesWithIds[value]);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
        setCurrentRangeStart(1);
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);

        const formattedDate = date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
        });

        const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });

        return `${formattedDate}, ${formattedTime}`;
    };

    const handleBack = () => {
        navigate(-1); // Go back to previous page in history
    };

    return (
        <div style={{ 
            fontFamily: '"Inter", sans-serif', 
            background: '#f8f9fd', 
            margin: 0, 
            padding: '20px', 
            minHeight: '100vh',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            <div style={{ 
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                boxSizing: 'border-box'
            }}>
                <div style={{ 
                    background: '#fff', 
                    borderRadius: '16px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', 
                    padding: '20px', 
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}>
                    {/* Header Section */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: '15px', 
                        flexWrap: 'wrap',
                        gap: '12px'
                    }}>
                        <h2 style={{ fontSize: '20px', margin: 0, fontWeight: 600 }}>
                            Error Logs
                        </h2>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            {/* Filter dropdown */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label style={{ fontSize: '14px', color: '#555', margin: 0 }}>Filter:</label>
                                <select
                                    value={selectedFilter}
                                    onChange={(e) => handleFilterChange(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        minWidth: '150px',
                                        maxWidth: '200px'
                                    }}
                                >
                                    {uniqueWebsites.map((website, index) => (
                                        <option key={index} value={website}>
                                            {website}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Refresh Button */}
                            <OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleRefresh}
                                    style={{
                                        background: 'white',
                                        border: '1px solid #ddd',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        fontSize: '14px',
                                        color: '#5a6acf',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                    }}
                                >
                                    <RefreshIcon />
                                </Button>
                            </OverlayTrigger>

                            {/* Back Button */}
                            <OverlayTrigger placement="top" overlay={<Tooltip>Back</Tooltip>}>
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleBack}
                                    style={{
                                        background: 'white',
                                        border: '1px solid #ddd',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        fontSize: '14px',
                                        color: '#5a6acf',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                    }}
                                >
                                    <BackIcon />
                                </Button>
                            </OverlayTrigger>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#555' }}>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '20%' }}>Client</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '20%' }}>Document</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '15%' }}>Error Area</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '25%' }}>Error Message</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '20%', textAlign: 'center' }}>Creation Timestamp</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: 'center', padding: '14px 10px' }}>
                                            <SectionLoader label="Loading error logs..." className="revize-section-loader--table" />
                                        </td>
                                    </tr>
                                ) : errorLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: 'center', padding: '14px 10px', color: '#888' }}>
                                            No error logs found.
                                        </td>
                                    </tr>
                                ) : (
                                errorLogs.map((log) => (
                                    <tr key={log.reference_id} style={{ cursor: 'pointer' }}>
                                        <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>
                                            {truncateText(log.website_name, 30)}
                                        </td>
                                        <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>
                                            {truncateText(log.doc_name, 50)}
                                        </td>
                                        <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>
                                            {log.error_area}
                                        </td>
                                        <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>
                                            <span
                                                onClick={() => openFullMessageModal(log)}
                                                style={{ 
                                                    cursor: 'pointer', 
                                                    textDecoration: 'underline',
                                                    color: '#5a6acf'
                                                }}
                                            >
                                                {truncateText(log.error_message, 50)}
                                            </span>
                                        </td>
                                        <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                            {formatTimestamp(log.creation_timestamp)}
                                        </td>
                                    </tr>
                                ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Section */}
                    {!isLoading && errorLogs.length > 0 && (
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginTop: '15px', 
                            fontSize: '14px', 
                            color: '#888',
                            flexWrap: 'wrap',
                            gap: '10px'
                        }}>
                            <div style={{ flex: '1 1 200px', textAlign: 'left' }}>
                                <span>Showing data {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, siteDataLength)} of {siteDataLength} entries</span>
                            </div>

                            <div style={{ 
                                flex: '1 1 auto', 
                                display: 'flex', 
                                justifyContent: 'center', 
                                gap: '6px', 
                                alignItems: 'center',
                                flexWrap: 'wrap'
                            }}>
                                {/* Left arrow - Always visible but disabled when at start */}
                                <button
                                    onClick={currentRangeStart > 1 ? handlePrevRange : undefined}
                                    disabled={currentRangeStart <= 1}
                                    style={{
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        background: currentRangeStart <= 1 ? '#f5f5f5' : '#fff',
                                        color: currentRangeStart <= 1 ? '#ccc' : 'inherit',
                                        cursor: currentRangeStart <= 1 ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                    }}
                                >
                                    &lt;
                                </button>

                                {visiblePageNumbers.map((number) => (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        style={{
                                            padding: '4px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            background: currentPage === number ? '#5A6ACF' : '#fff',
                                            color: currentPage === number ? '#fff' : 'inherit',
                                            cursor: 'pointer',
                                            fontSize: '13px'
                                        }}
                                    >
                                        {number}
                                    </button>
                                ))}

                                {/* Right arrow - Always visible but disabled when at end */}
                                <button
                                    onClick={currentRangeStart + pagesToShow <= totalPages ? handleNextRange : undefined}
                                    disabled={currentRangeStart + pagesToShow > totalPages}
                                    style={{
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        background: currentRangeStart + pagesToShow > totalPages ? '#f5f5f5' : '#fff',
                                        color: currentRangeStart + pagesToShow > totalPages ? '#ccc' : 'inherit',
                                        cursor: currentRangeStart + pagesToShow > totalPages ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                    }}
                                >
                                    &gt;
                                </button>
                            </div>

                            <div style={{ flex: '1 1 200px', textAlign: 'right' }}>
                                <label htmlFor="itemsPerPage" style={{ marginRight: '10px' }}>Items per page:</label>
                                <select
                                    id="itemsPerPage"
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                    style={{
                                        padding: '6px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        height: '32px',
                                        width: '80px',
                                        fontSize: '13px',
                                        appearance: 'menulist'
                                    }}
                                >
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={150}>150</option>
                                    <option value={200}>200</option>
                                    <option value={500}>500</option>
                                    <option value={1000}>1000</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal */}
                {selectedLog && (
                    <Modal show={showModal} onHide={handleCloseModal} size="xl">
                        <Modal.Header closeButton>
                            <Modal.Title>Full Error Details</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {selectedLog.error_message && (
                                <div className="mt-3">
                                    <h5>Error Message:</h5>
                                    <pre style={{
                                        backgroundColor: '#f4f4f4',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        {selectedLog.error_message}
                                    </pre>
                                </div>
                            )}

                            {selectedLog.stack_trace && (
                                <div className="mt-3">
                                    <h5>Stacktrace:</h5>
                                    <pre style={{
                                        backgroundColor: '#f4f4f4',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        {selectedLog.stack_trace}
                                    </pre>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button 
                                variant="secondary" 
                                onClick={handleCloseModal}
                                style={{ border: '1px solid #ddd' }}
                            >
                                Close
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}
            </div>
        </div>
    );
};

export default ErrorLogs;